import { addDays } from '@dairy/shared';

export interface ShareGrantPeriod {
  startDate: string;
  endDate?: string | null;
  percentBp: number;
}

export interface PartnerContributionValue {
  partnerId: string;
  date: string;
  valueSantim: number;
  deletedAt?: string | null;
}

export interface PartnerGrant extends ShareGrantPeriod {
  partnerId: string;
  deletedAt?: string | null;
}

export interface PartnerShare {
  partnerId: string;
  valueSantim: number;
  grantBp: number;
  numerator: bigint;
  denominator: bigint;
}

/** D-16: special shares are reserved first; contributions divide the remainder. */
export function sharesOn(date: string, contributions: readonly PartnerContributionValue[], grants: readonly PartnerGrant[]): PartnerShare[] | null {
  const values = new Map<string, number>();
  const special = new Map<string, number>();
  for (const c of contributions) if (!c.deletedAt && c.date <= date) values.set(c.partnerId, (values.get(c.partnerId) ?? 0) + c.valueSantim);
  for (const g of grants) if (!g.deletedAt && g.startDate <= date && (!g.endDate || g.endDate >= date)) special.set(g.partnerId, (special.get(g.partnerId) ?? 0) + g.percentBp);
  const total = [...values.values()].reduce((sum, value) => sum + BigInt(value), 0n);
  if (total === 0n) return null;
  const totalBp = [...special.values()].reduce((sum, value) => sum + value, 0);
  if (totalBp > 10_000) throw new Error('Special shares exceed 100%');
  const partnerIds = new Set([...contributions.filter((c) => !c.deletedAt).map((c) => c.partnerId), ...grants.filter((g) => !g.deletedAt).map((g) => g.partnerId)]);
  return [...partnerIds].map((partnerId) => {
    const valueSantim = values.get(partnerId) ?? 0;
    const grantBp = special.get(partnerId) ?? 0;
    return { partnerId, valueSantim, grantBp, numerator: BigInt(valueSantim) * BigInt(10_000 - totalBp) + BigInt(grantBp) * total, denominator: total * 10_000n };
  });
}

/** Dates on which a contribution or grant changes the share calculation. */
export function shareChangeDates(contributions: readonly PartnerContributionValue[], grants: readonly PartnerGrant[]): string[] {
  return [...new Set([
    ...contributions.filter((c) => !c.deletedAt).map((c) => c.date),
    ...grants.filter((g) => !g.deletedAt).flatMap((g) => [g.startDate, ...(g.endDate ? [addDays(g.endDate, 1)] : [])]),
  ])].sort();
}

export function shareHistory(contributions: readonly PartnerContributionValue[], grants: readonly PartnerGrant[]) {
  return shareChangeDates(contributions, grants).map((from) => ({ from, shares: sharesOn(from, contributions, grants) }));
}

/** Largest-remainder allocation, with name and id as deterministic tie-breakers. */
export function splitByShares(amountSantim: number, shares: readonly PartnerShare[], partners: readonly { id: string; name: string }[]) {
  if (!Number.isSafeInteger(amountSantim) || amountSantim < 0) throw new Error('Invalid amount');
  if (!shares.length) return [];
  const names = new Map(partners.map((p) => [p.id, p.name]));
  const allocated = shares.map((share) => {
    const exact = BigInt(amountSantim) * share.numerator;
    return { partnerId: share.partnerId, amountSantim: Number(exact / share.denominator), sharePpm: Number((share.numerator * 2_000_000n + share.denominator) / (2n * share.denominator)), remainder: exact % share.denominator };
  });
  let left = amountSantim - allocated.reduce((sum, row) => sum + row.amountSantim, 0);
  const order = [...allocated].sort((a, b) => a.remainder === b.remainder
    ? (names.get(a.partnerId) ?? '').localeCompare(names.get(b.partnerId) ?? '') || a.partnerId.localeCompare(b.partnerId)
    : a.remainder > b.remainder ? -1 : 1);
  for (const row of order) { if (left <= 0) break; row.amountSantim++; left--; }
  return allocated.map(({ remainder: _remainder, ...row }) => row);
}

export function formatShare(numerator: bigint, denominator: bigint, decimals = 2): string {
  if (denominator <= 0n || decimals < 0 || !Number.isInteger(decimals)) throw new Error('Invalid share');
  const scale = 10n ** BigInt(decimals);
  const rounded = (numerator * 200n * scale + denominator) / (2n * denominator);
  return decimals ? `${rounded / scale}.${String(rounded % scale).padStart(decimals, '0')}` : String(rounded);
}

/** Maximum special-share total over an inclusive date range. */
export function maxActiveGrantBp(grants: readonly ShareGrantPeriod[], from: string, to: string | null): number {
  return peakActiveGrant(grants, from, to).percentBp;
}

/** First date attaining the highest total, for an actionable overlap warning. */
export function peakActiveGrant(grants: readonly ShareGrantPeriod[], from: string, to: string | null): { date: string; percentBp: number } {
  const points = [from, ...grants.map((grant) => grant.startDate).filter((date) => date >= from && (!to || date <= to))];
  return [...new Set(points)].sort().reduce((peak, date) => {
    const percentBp = grants.reduce((sum, grant) => sum + (grant.startDate <= date && (!grant.endDate || grant.endDate >= date) ? grant.percentBp : 0), 0);
    return percentBp > peak.percentBp ? { date, percentBp } : peak;
  }, { date: from, percentBp: 0 });
}

/** Partner cash is separate from operating income and expenses (D-11). */
export function partnerCashStats(
  cashFlowSantim: number,
  contributions: readonly { date: string; cashReceivedSantim: number; deletedAt?: string | null }[],
  distributions: readonly { deletedAt?: string | null; lines: readonly { amountSantim: number; paidOn?: string | null }[] }[],
  from: string,
  to: string,
) {
  const partnerCashInSantim = contributions.reduce((sum, c) => sum + (!c.deletedAt && c.date >= from && c.date <= to ? c.cashReceivedSantim : 0), 0);
  const distributionsPaidSantim = distributions.reduce((sum, d) => sum + (d.deletedAt ? 0 : d.lines.reduce((lineSum, line) => lineSum + (line.paidOn && line.paidOn >= from && line.paidOn <= to ? line.amountSantim : 0), 0)), 0);
  return { partnerCashInSantim, distributionsPaidSantim, netCashAfterPartnersSantim: cashFlowSantim + partnerCashInSantim - distributionsPaidSantim };
}
